import DataPointsLib, { type DataPoints } from '@retracedgmbh/data-points-lib';
import {
  AMFORI_BSCI_COMPOSITE_CODE_MAP,
  AMFORI_BSCI_DATAPOINT_MAP,
  type AmforiBsciDatapointMapping,
} from '../../constants.js';

const dataPointsLib: DataPoints = new DataPointsLib(
  console,
).initializeDataPoints();

/** The amfori `MonitoringDatapoint.type` discriminator. */
export type AmforiDatapointType =
  | 'predefined_value'
  | 'text'
  | 'number'
  | 'date'
  | 'time'
  | 'datetime'
  | 'composite';

/**
 * One raw amfori `MonitoringDatapoint` as pulled from the API (the JSON-encoded
 * `valueAsJson` string is kept verbatim — this util parses it). The producing
 * datapoints phase is a separate sub-task; the writer owns this contract.
 */
export interface AmforiMonitoringDatapoint {
  key: string;
  type: AmforiDatapointType;
  valueAsJson: string;
  /**
   * The datapoint's English label, carried inline on the amfori
   * `/monitoring-datapoint` record. Used to match against
   * `AMFORI_BSCI_DATAPOINT_MAP`; `null` when amfori omits it (→ unmapped).
   */
  label: string | null;
  /** Set on child rows of a composite repeating-group, e.g. `<parentUuid>-15`. */
  compositeIdPath?: string | null;
  datapointValueCompositeDatapointId?: string | null;
}

export interface DecodeAmforiDatapointsInput {
  datapoints: AmforiMonitoringDatapoint[];
  /** Audit-level currency/year used to normalize `currency_with_year` metrics. */
  metadata: { currency: string | null; year: number | null };
}

export interface DatapointValidationFailure {
  dataPointId: string;
  amforiLabel: string;
  error: string;
}

export interface ExtractedDatapoints {
  key_metrics: Record<string, Record<string, unknown>>;
  other_metrics: Record<string, Record<string, unknown>>;
  metadata: { currency: string | null; year: number | null };
}

/** One decoded top-level (non composite-child) datapoint, kept in the raw payload. */
interface DecodedDatapoint {
  type: AmforiDatapointType;
  label: string | null;
  value: unknown;
}

export interface ExternalRawPayload {
  /** Every top-level amfori datapoint, decoded, keyed by amfori `key`. */
  datapoints: Record<string, DecodedDatapoint>;
  /** Composite repeating-groups, one array entry per row, keyed by group prefix. */
  composites: Record<string, Array<Record<string, unknown>>>;
}

export interface DecodeAmforiDatapointsResult {
  extractedDatapoints: ExtractedDatapoints;
  externalRawPayload: ExternalRawPayload;
  validationFailures: DatapointValidationFailure[];
}

const KEY_METRICS_CATEGORY = 'Key Metrics';
/** `<parentUuid>-15` → strip the trailing `-<index>` to get the group prefix. */
const COMPOSITE_INDEX_SUFFIX = /-(\d+)$/;

/** Normalize a thrown validation error into a message string. */
export const toValidationFailureMessage = (err: unknown): string =>
  err instanceof Error ? err.message : 'Validation failed';

/** Parse `valueAsJson` into a typed object/array; never throws (raw string on failure). */
const parseValueAsJson = (valueAsJson: string): unknown => {
  try {
    return JSON.parse(valueAsJson);
  } catch {
    return valueAsJson;
  }
};

/** Decode a single datapoint's value according to its amfori `type`. */
const decodeValue = (dp: AmforiMonitoringDatapoint): unknown => {
  const parsed = parseValueAsJson(dp.valueAsJson) as Record<string, unknown>;
  switch (dp.type) {
    case 'predefined_value':
      return { label: parsed?.label ?? null, value: parsed?.value ?? null };
    case 'text':
    case 'number':
      return { value: parsed?.value ?? null };
    case 'date':
    case 'time':
    case 'datetime':
      return parsed ?? null;
    case 'composite':
      // The parent's value is the array of child sub-row UUIDs.
      return parseValueAsJson(dp.valueAsJson);
    default:
      return parsed ?? null;
  }
};

/**
 * Build the data-points-lib answer value, formatted for the *target* schema's
 * `fieldTypeId` (mirrors `format-bsci-datapoints.ts`). The amfori `valueType` is
 * not authoritative — e.g. `total_number_of_workers` is `'number'` in the amfori
 * map but a `SHORT_TEXT` field in data-points-lib, so its value must be a string.
 */
const buildAnswerValue = (
  fieldTypeId: string,
  decoded: unknown,
  metadata: { currency: string | null; year: number | null },
): Record<string, unknown> => {
  const rawValue = (decoded as { value?: unknown })?.value;

  switch (fieldTypeId) {
    case 'SHORT_TEXT':
      return { value: rawValue == null ? null : String(rawValue) };
    case 'NUMBER_DYNAMIC_CURRENCY_WITH_YEAR':
      return {
        value: Number(rawValue),
        unit: metadata.currency,
        year: metadata.year,
      };
    default:
      // NUMBER (and any other numeric field) → a plain numeric value.
      return { value: Number(rawValue) };
  }
};

/**
 * Decodes one amfori Monitoring's datapoints into the strict
 * `{key_metrics, other_metrics, metadata}` shape (mapped + schema-passing
 * subset) plus the full raw payload (everything, including unmapped datapoints
 * and reconstructed composite repeating-groups). Mapped values that fail schema
 * validation are omitted from `extractedDatapoints` but kept in the raw payload
 * and reported in `validationFailures`.
 */
export const decodeAmforiDatapoints = async (
  input: DecodeAmforiDatapointsInput,
): Promise<DecodeAmforiDatapointsResult> => {
  const { datapoints, metadata } = input;

  const mappingByLabel = new Map<string, AmforiBsciDatapointMapping>();
  const mappingByDataPointId = new Map<string, AmforiBsciDatapointMapping>();
  for (const entry of AMFORI_BSCI_DATAPOINT_MAP) {
    mappingByLabel.set(entry.amforiLabel, entry);
    mappingByDataPointId.set(entry.dataPointId, entry);
  }

  const extractedDatapoints: ExtractedDatapoints = {
    key_metrics: {},
    other_metrics: {},
    metadata,
  };
  const externalRawPayload: ExternalRawPayload = {
    datapoints: {},
    composites: {},
  };
  const validationFailures: DatapointValidationFailure[] = [];

  /**
   * Validates one mapped datapoint's value against data-points-lib and files it
   * into the matching bucket, or records a `validationFailure`. Shared by the
   * top-level (label-matched) and composite (code-matched) paths. First source
   * wins — a dataPointId already bucketed is left untouched (headcounts arrive
   * only via composite, wage/sample only via label, so this is just a guard).
   */
  const mapAndBucket = async (
    mapping: AmforiBsciDatapointMapping,
    decoded: unknown,
  ): Promise<void> => {
    if (
      mapping.dataPointId in extractedDatapoints.key_metrics ||
      mapping.dataPointId in extractedDatapoints.other_metrics
    ) {
      return;
    }
    try {
      const dataPoint = dataPointsLib.getDataPointById(mapping.dataPointId);
      const fieldTypeId = dataPoint.dataPointDefinition.fieldTypeId;
      const answerValue = buildAnswerValue(fieldTypeId, decoded, metadata);
      await dataPointsLib.validate(mapping.dataPointId, answerValue, {});
      const bucket =
        mapping.category === KEY_METRICS_CATEGORY
          ? extractedDatapoints.key_metrics
          : extractedDatapoints.other_metrics;
      bucket[mapping.dataPointId] = answerValue;
    } catch (err) {
      validationFailures.push({
        dataPointId: mapping.dataPointId,
        amforiLabel: mapping.amforiLabel,
        error: toValidationFailureMessage(err),
      });
    }
  };

  for (const dp of datapoints) {
    const decoded = decodeValue(dp);

    // Composite child row → reconstruct into its repeating-group (raw payload).
    // If its column code maps a headcount datapoint (Employment Structure grid),
    // also validate + bucket it — that is where amfori delivers the workforce
    // breakdown (only wage/sample datapoints arrive as top-level labels).
    if (dp.compositeIdPath) {
      const match = dp.compositeIdPath.match(COMPOSITE_INDEX_SUFFIX);
      const prefix = dp.compositeIdPath.replace(COMPOSITE_INDEX_SUFFIX, '');
      const rowIndex = match ? Number(match[1]) : 0;

      const group = externalRawPayload.composites[prefix] ?? [];
      externalRawPayload.composites[prefix] = group;
      const row = group[rowIndex] ?? {};
      group[rowIndex] = row;
      row[dp.key] = decoded;

      const compositeDataPointId = AMFORI_BSCI_COMPOSITE_CODE_MAP[dp.key];
      const compositeMapping = compositeDataPointId
        ? mappingByDataPointId.get(compositeDataPointId)
        : undefined;
      if (compositeMapping) await mapAndBucket(compositeMapping, decoded);
      continue;
    }

    const label = dp.label ?? null;
    externalRawPayload.datapoints[dp.key] = {
      type: dp.type,
      label,
      value: decoded,
    };

    // Unmapped datapoints are never validated — raw payload only (per Q-15).
    const mapping = label ? mappingByLabel.get(label) : undefined;
    if (mapping) await mapAndBucket(mapping, decoded);
  }

  return { extractedDatapoints, externalRawPayload, validationFailures };
};
